import {
  Box,
  Flex,
  FlexProps,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  LinkBox,
  LinkOverlay,
  LinkProps,
  Spacer,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { useDesktopMenuStyle } from 'apps/TenantApp/theme/components/DesktopMenu';
import { Card } from 'components/cards/Card';
import { FeedbackModal } from 'components/FeedbackModal';
import {
  AlphaIcon,
  CalendarIcon,
  CommentAltIcon,
  EllipsisVIcon,
  HelpIcon,
  HomeIcon,
  NavigateBackIcon,
  NavigateForwardIcon,
  NotificationIcon,
  SearchIcon,
  SettingsWheelIcon,
  TodoIcon,
  VaultIcon,
  VaultManagerIcon,
} from 'components/icons';
import { useLocalStorage } from 'hooks/useBrowserStorage';
import { useSidecarContext } from 'providers/SidecarProvider';
import { useUnreadChatMessagesContext } from 'providers/UnreadChatMessagesProvider';
import { useUnreadNotificationsContext } from 'providers/UnreadNotificationsProvider';
import React, { FC } from 'react';
import { matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { graphql, useSafeRelay } from 'utils/graphClient';
import {
  CALENDAR_PATH,
  HOME_PATH,
  ORG_SETTINGS_PARAM_NAME,
  SETTINGS_DEFAULT_PATH,
  TODO_PATH,
  VAULTS_MANAGER_PATH,
  VAULTS_MY_VAULT_PATH,
  VAULTS_PATH,
} from 'utils/routes';
import { NotificationBubble } from '../notifications/NotificationBubble';
import { useSearchModal } from '../SearchModal/useSearchModal';
import AccountMenu from './AccountMenu';
import { CollapsingMenuSection } from './CollapsingMenuSection';
import { DesktopMenuContext, useDesktopMenu } from './DesktopMenuContext';
import { FavouritesMenuSection } from './FavouritesMenuSection';
import Logo from './Logo';
import { MenuItemStack } from './MenuItemStack';
import { MenuSubtitle } from './MenuSubtitle';
import { SpacesMenuSection } from './SpacesMenuSection';
import { DesktopMenuQuery } from './__generated__/DesktopMenuQuery.graphql';

const MenuDataQuery = graphql`
  query DesktopMenuQuery {
    sessionInfo {
      ...AccountMenuSessionInfo
      ...LogoSessionInfo
      canManageVaults {
        value
      }
      tenant {
        canUpdate {
          value
        }
        name
      }
      member {
        spaceMemberships(first: 500)
          @connection(key: "Session_spaceMemberships") {
          edges {
            node {
              id
            }
          }
          ...SpacesMenuSection_spaces
        }
      }
      ...FavouritesMenuSection
    }
  }
`;

const FakeSearchField = ({
  onClick,
  compact,
}: {
  onClick: () => void;
  compact: boolean;
}) => {
  if (compact) {
    return (
      <MenuItemStack>
        <MenuLink
          to=""
          label={t`Search`}
          icon={<SearchIcon />}
          onClick={onClick}
        />
      </MenuItemStack>
    );
  } else {
    return (
      <InputGroup
        size="sm"
        variant="filled"
        onClick={onClick}
        cursor="text"
        color="gray.400"
      >
        <InputRightElement>
          <SearchIcon />
        </InputRightElement>
        <Input as="div" rounded="md" py={1}>{t`Search`}</Input>
      </InputGroup>
    );
  }
};

interface CollapseButtonProps {
  isMenuOpen: boolean;
  onCollapse: () => void;
  onOpen: () => void;
}

const CollapseButton: FC<CollapseButtonProps> = ({
  isMenuOpen,
  onCollapse,
  onOpen,
}) => {
  const color = useColorModeValue('gray.700', 'white');

  return (
    <IconButton
      transition="all .3s"
      size="xs"
      borderRadius="full"
      colorScheme="gray"
      color={color}
      zIndex={1}
      sx={{
        opacity: 0,
        '&:hover, .mainMenu:hover &': {
          opacity: 1,
        },
      }}
      icon={isMenuOpen ? <NavigateBackIcon /> : <NavigateForwardIcon />}
      aria-label={isMenuOpen ? t`Collapse menu` : t`Open menu`}
      onClick={isMenuOpen ? onCollapse : onOpen}
      position="absolute"
      right={-3}
      top="50%"
    />
  );
};

const Menu: FC<FlexProps> = (props) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const data = useLazyLoadQuery<DesktopMenuQuery>(MenuDataQuery, {});
  const styles = useDesktopMenuStyle();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { isCollapsed, setIsCollapsed } = usePersistedDesktopMenuCollapse();
  const { hasUnreadChatMessages } = useUnreadChatMessagesContext();
  const { hasUnreadNotifications } = useUnreadNotificationsContext();
  const { setActiveSidecarApp } = useSidecarContext();
  const { openSearch } = useSearchModal();
  const {
    isOpen: isFeedbackOpen,
    onOpen: onOpenFeedback,
    onClose: onCloseFeedback,
  } = useDisclosure();

  if (!data.sessionInfo.tenant) return null;

  const width = !isCollapsed ? 72 : 20;

  const isHomeActive = !!matchPath(HOME_PATH, pathname);
  const isCalendarActive = pathname.startsWith(CALENDAR_PATH);
  const isTodosActive = !!matchPath(TODO_PATH, pathname);
  const isMyVaultActive = !!matchPath(VAULTS_PATH, pathname);
  const isPersonalActive =
    isHomeActive || isCalendarActive || isTodosActive || isMyVaultActive;

  const isOrgSettingsActive = !!params.get(ORG_SETTINGS_PARAM_NAME);
  const isVaultManagerActive = pathname.startsWith(VAULTS_MANAGER_PATH);
  const isMoreActive = isOrgSettingsActive || isVaultManagerActive;

  return (
    <DesktopMenuContext.Provider value={{ isCollapsed }}>
      <Flex
        className="mainMenu"
        sx={styles.menu}
        as="nav"
        pos="relative"
        minW={width}
        maxW={72}
        {...props}
      >
        <CollapseButton
          key="collapse-button"
          isMenuOpen={!isCollapsed}
          onCollapse={() => setIsCollapsed(true)}
          onOpen={() => setIsCollapsed(false)}
        />
        <Flex
          as="nav"
          direction="column"
          flex="1"
          style={{ gap: 'var(--chakra-space-8)' }}
          overflowY="auto"
          aria-label={t`Sidebar Navigation`}
        >
          <Stack spacing={isCollapsed ? 16 : 4}>
            <Logo sessionInfo={data.sessionInfo} />
            <Box px={4}>
              <FakeSearchField
                onClick={() => openSearch()}
                compact={isCollapsed}
              />
            </Box>
          </Stack>
          <Flex
            flex={1}
            px={4}
            pb={4}
            direction="column"
            style={{ gap: 'var(--chakra-space-8)' }}
          >
            <CollapsingMenuSection
              label={t`Personal`}
              icon={<HomeIcon />}
              hasUnread={hasUnreadNotifications || hasUnreadNotifications}
              isActive={isPersonalActive}
            >
              <MenuItemStack>
                <MenuSubtitle>{t`Personal`}</MenuSubtitle>
                <MenuLink
                  to={HOME_PATH}
                  label={t`Home`}
                  icon={<HomeIcon />}
                  isActive={isHomeActive}
                />
                <MenuLink
                  label={t`Notifications`}
                  to=""
                  onClick={() => {
                    setActiveSidecarApp('NOTIFICATIONS');
                  }}
                  icon={<NotificationIcon />}
                  hasUnread={hasUnreadNotifications}
                />
                <MenuLink
                  label={t`Chat`}
                  to=""
                  icon={<CommentAltIcon />}
                  hasUnread={hasUnreadChatMessages}
                  onClick={() => {
                    setActiveSidecarApp('CHAT');
                  }}
                />
                <MenuLink
                  label={t`Calendar`}
                  to={CALENDAR_PATH}
                  icon={<CalendarIcon />}
                  isActive={isCalendarActive}
                />
                <MenuLink
                  label={t`To do`}
                  to={TODO_PATH}
                  icon={<TodoIcon />}
                  isActive={isTodosActive}
                />
                <MenuLink
                  label={t`My vaults`}
                  to={VAULTS_MY_VAULT_PATH}
                  icon={<VaultIcon />}
                  isActive={isMyVaultActive}
                />
              </MenuItemStack>
            </CollapsingMenuSection>

            <FavouritesMenuSection favouritesFragment={data.sessionInfo} />

            <SpacesMenuSection
              spaces={data.sessionInfo.member?.spaceMemberships}
              canCreateSpace={!!data.sessionInfo.tenant.canUpdate.value}
            />

            <Spacer flexGrow="1" />

            <CollapsingMenuSection
              label={t`More`}
              icon={<EllipsisVIcon />}
              isActive={isMoreActive}
            >
              <MenuItemStack>
                <MenuSubtitle>{t`More`}</MenuSubtitle>
                {data.sessionInfo.tenant.canUpdate.value && (
                  <MenuLink
                    label={t`Organisation settings`}
                    icon={<SettingsWheelIcon />}
                    to={`?${ORG_SETTINGS_PARAM_NAME}=${SETTINGS_DEFAULT_PATH}`}
                    isActive={isOrgSettingsActive}
                  />
                )}
                {data.sessionInfo.canManageVaults.value && (
                  <MenuLink
                    label={t`Vault manager`}
                    icon={<VaultManagerIcon />}
                    to={VAULTS_MANAGER_PATH}
                    isActive={isVaultManagerActive}
                  />
                )}
                <MenuLink
                  toExternal="https://support.trustedfamily.net/help"
                  label={t`Help center`}
                  icon={<HelpIcon />}
                />
                <MenuLink
                  onClick={onOpenFeedback}
                  to=""
                  label={t`Provide feedback`}
                  icon={<AlphaIcon />}
                  isActive={isFeedbackOpen}
                />
                <FeedbackModal
                  isOpen={isFeedbackOpen}
                  onClose={onCloseFeedback}
                />
              </MenuItemStack>
            </CollapsingMenuSection>
          </Flex>
        </Flex>

        <AccountMenu sessionInfo={data.sessionInfo} />
      </Flex>
    </DesktopMenuContext.Provider>
  );
};

export default Menu;

export interface BaseMenuLinkProps {
  isActive?: boolean;
  isDisabled?: boolean;
  hasUnread?: boolean;
  label: string;
  icon: React.ReactNode;
  isMobile?: boolean;
}

export type MenuLinkProps = BaseMenuLinkProps & LinkProps;

export const MenuLink = (props: MenuLinkProps) => {
  const { icon, isActive, isDisabled, hasUnread, label, isMobile, ...rest } =
    props;
  const styles = useDesktopMenuStyle();
  const { isCollapsed } = useDesktopMenu();

  if (isMobile) {
    return <MobileMenuLink {...props} />;
  }

  let color = 'inherit';
  if (isDisabled) {
    color = 'gray.500';
  }

  const sx = {
    ...styles.item,
    _hover: {
      ...(styles.item as any)._hover,
      textDecoration: 'none',
      '> *': {
        transform: isCollapsed ? 'none' : 'translateX(4px)',
      },
    },
    paddingX: isCollapsed ? 2 : isActive ? 3 : 2,
  };

  return (
    <Link
      color={color}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      userSelect="none"
      aria-current={isActive ? 'page' : undefined}
      sx={sx}
      {...rest}
      w={isCollapsed ? 'unset' : '100%'}
      pos="relative"
    >
      <HStack
        spacing="2"
        alignItems="center"
        fontWeight={hasUnread ? 'bold' : 'normal'}
        transition="all 0.3s"
      >
        <Box fontSize={isCollapsed ? '1.4em' : '1.2em'}>{icon}</Box>
        {!isCollapsed && <Text as="span">{label}</Text>}
      </HStack>
      <NotificationBubble
        hasUnread={hasUnread}
        pos="absolute"
        right={isCollapsed ? 1 : 3}
        top={isCollapsed ? 1 : 3}
      />
    </Link>
  );
};

const MobileMenuLink = (props: MenuLinkProps) => {
  const { icon, isActive, isDisabled, hasUnread, label, isMobile, ...rest } =
    props;
  return (
    <LinkBox h="100%">
      <Card h="100%">
        <HStack p={2}>
          <Box fontSize="1.2em" color="blue.500">
            {icon}
          </Box>
          <LinkOverlay as={Link} {...rest}>
            {label}
          </LinkOverlay>
        </HStack>
      </Card>
    </LinkBox>
  );
};

const DESKTOP_COLLAPSE_STATE_STORAGE_KEY = 'desktop-collapse-state';

const usePersistedDesktopMenuCollapse = (): {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
} => {
  const [rawIsCollapsed, setIsCollapsed] = useLocalStorage<
    'false' | 'true' | false | true
  >(DESKTOP_COLLAPSE_STATE_STORAGE_KEY, false);
  const isCollapsed =
    typeof rawIsCollapsed === 'boolean'
      ? rawIsCollapsed
      : JSON.parse(rawIsCollapsed);

  return { isCollapsed, setIsCollapsed };
};
